import React, { useState, useMemo, useEffect } from "react";
import { createTheme, ThemeProvider } from "@mui/material";
import AdronTheme from "../theme/AdronTheme";
import { APP_ID } from "../_app/consts";

type Mode = "light" | "dark" | "system";

interface ColorModeContextType {
  activeMode: Mode;
  setLightMode: () => void;
  setDarkMode: () => void;
  setSystemMode: () => void;
  toggleColorMode: () => void;
}

export const ColorModeContext = React.createContext<ColorModeContextType>({
  activeMode: "light",
  setLightMode: () => {},
  setDarkMode: () => {},
  setSystemMode: () => {},
  toggleColorMode: () => {},
});

function getSystemMode(): "light" | "dark" {
  const prefersDarkMode =
    window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
  return prefersDarkMode ? "dark" : "light";
}

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeMode, setActiveMode] = useState<Mode>(() => {
    const savedMode = localStorage.getItem("activeMode" + APP_ID);
    return savedMode ? (savedMode as Mode) : "system";
  });

  const actualMode = useMemo(() => {
    return activeMode === "system" ? getSystemMode() : activeMode;
  }, [activeMode]);

  const colorMode = useMemo(
    () => ({
      activeMode,
      setLightMode: () => {
        setActiveMode("light");
        localStorage.setItem("activeMode" + APP_ID, "light");
      },
      setDarkMode: () => {
        setActiveMode("dark");
        localStorage.setItem("activeMode" + APP_ID, "dark");
      },
      setSystemMode: () => {
        setActiveMode("system");
        localStorage.setItem("activeMode" + APP_ID, "system");
      },
      toggleColorMode: () => {
        const newMode = actualMode === "light" ? "dark" : "light";
        setActiveMode(newMode);
        localStorage.setItem("activeMode" + APP_ID, newMode);
      },
    }),
    [activeMode, actualMode]
  );

  const theme = useMemo(() => AdronTheme(actualMode), [actualMode]);

  useEffect(() => {
    document.body.style.backgroundColor = theme.palette.background.default;
  }, [theme]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ColorModeContext.Provider>
  );
};
